import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { environment } from '@environment/environment';

export interface BeforeInstallPromptEvent extends Event {
	readonly platforms: string[];
	readonly userChoice: Promise<{
		outcome: 'accepted' | 'dismissed';
		platform: string;
	}>;
	prompt(): Promise<void>;
}

@Injectable({
	providedIn: 'root',
})
export class PwaInstallService {
	private deferredPrompt: BeforeInstallPromptEvent | null = null;
	private canInstall$ = new BehaviorSubject<boolean>(false);
	private _shouldShowPrompt$ = new BehaviorSubject<boolean>(false);

	// Public observable for components to subscribe to
	public shouldShowPrompt$ = this._shouldShowPrompt$.asObservable();

	constructor(private cargoChief: Cargochief) {
		this.setupInstallPromptListener();
	}

	private setupInstallPromptListener(): void {
		window.addEventListener('beforeinstallprompt', (e: Event) => {
			e.preventDefault();
			this.deferredPrompt = e as BeforeInstallPromptEvent;
			this.canInstall$.next(true);
		});

		// Listen for app installation
		window.addEventListener('appinstalled', () => {
			this.deferredPrompt = null;
			this.canInstall$.next(false);
		});
	}

	/**
	 * Returns an observable that emits true when the app can be installed
	 * Emits immediately with current state, then continues to emit on changes
	 */
	public canInstall(): Observable<boolean> {
		// Return the BehaviorSubject directly so subscribers get the current value immediately
		// BehaviorSubject emits the last value to new subscribers
		return this.canInstall$.asObservable();
	}

	public isPromptAvailable(): boolean {
		return this.deferredPrompt !== null;
	}

	public async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
		if (!this.deferredPrompt) {
			return 'unavailable';
		}

		// Show the install prompt
		await this.deferredPrompt.prompt();

		// Wait for the user to respond to the prompt
		const { outcome } = await this.deferredPrompt.userChoice;

		// Clear the saved prompt since it can only be used once
		this.deferredPrompt = null;
		this.canInstall$.next(false);

		return outcome;
	}

	public isInstalled(): boolean {
		return (
			window.matchMedia('(display-mode: standalone)').matches ||
			(window.navigator as any).standalone
		);
	}

	public dismissPrompt(): void {
		this.deferredPrompt = null;
		this.canInstall$.next(false);
	}

	isIOS(): boolean {
		const ua = navigator.userAgent;
		return /iphone|ipad|ipod/i.test(ua);
	}

	isMobileDevice(): boolean {
		// Check using user agent
		const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

		// Check for mobile devices
		const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
		const isMobileUA = mobileRegex.test(userAgent.toLowerCase());

		// Additional check for touch screen
		const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

		// Check screen width (typical mobile width)
		const isMobileWidth = window.innerWidth <= 768;

		return isMobileUA || (isTouchDevice && isMobileWidth);
	}

	public updatePromptVisibility(userId: string | null): void {
		const isMobile = this.isMobileDevice();
		const shouldShow = !!userId && environment.FEATURE_FLAGS.ENABLE_PWA && isMobile;

		this._shouldShowPrompt$.next(shouldShow);
	}

	public clearDismissalFlag(): void {
		this.cargoChief.removeItem(CARGOCHIEF_KEYS.PWA_INSTALL_DISMISSED);
	}

	public wasDismissedThisSession(): boolean {
		return this.cargoChief.getItem(CARGOCHIEF_KEYS.PWA_INSTALL_DISMISSED) === 'true';
	}

	public markDismissedThisSession(): void {
		this.cargoChief.setItem({ [CARGOCHIEF_KEYS.PWA_INSTALL_DISMISSED]: 'true' });
	}
}
